import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs/inputText';
import { MakeCopy16 } from 'ui/icons/aurora/16px/MakeCopy16';
import { Confirm16 } from 'ui/icons/aurora/16px/Confirm16';
import { colors } from 'ui/theme/colors';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { pxToRem } from 'utils/convert';

import { useTranslations } from '../../../hooks/useTranslations';

const StyledInputText = styled(InputText)`
  background-color: ${colors.backgroundDisabled};
  border-color: ${colors.borderPrimary};

  input {
    background-color: transparent !important;
    text-overflow: ellipsis;
  }
`;

// icon wrapper increases clickable area of the suffix
const IconWrapper = styled.div`
  display: flex;
  height: ${pxToRem(22)};
  width: ${pxToRem(22)};
  align-items: center;
  justify-content: center;
`;

export type Props = {
  value: string;
};

export const ids = {
  inputImgURL: 'cl-input-image-url',
};

export const testIds = {
  copyBtn: 'cl-input-image-url-copy-btn',
  copyBtnCopiedIcon: 'cl-input-image-url-copy-btn-copied-icon',
  copyBtnCopyIcon: 'cl-input-image-url-copy-btn-copy-icon',
  inputImgURL: ids.inputImgURL,
};

export const InputImageURL = ({ value }: Props) => {
  const { t } = useTranslations();
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>();

  // Show 'copied' indicator, then revert after 3 seconds
  const onCopy = useCallback(() => {
    if (copiedTimer.current) {
      clearTimeout(copiedTimer.current);
    }
    setIsCopied(true);
    copiedTimer.current = setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (copiedTimer.current) {
        clearTimeout(copiedTimer.current);
      }
    };
  }, []);

  return (
    <FormField inputId={ids.inputImgURL} labelText={t('input.imageURL.label')}>
      <StyledInputText
        data-test={testIds.inputImgURL}
        id={ids.inputImgURL}
        isDisabled={true}
        Suffix={
          <Tooltip
            tooltipContent={t(`tooltip.${isCopied ? 'copied' : 'copy'}`)}
            open={isCopied || undefined}
          >
            <CopyToClipboard text={value} onCopy={onCopy} data-test={testIds.copyBtn}>
              <IconWrapper>
                {isCopied ? (
                  <Confirm16
                    className="shape"
                    data-test={testIds.copyBtnCopiedIcon}
                    isColorInherited
                  />
                ) : (
                  <MakeCopy16 data-test={testIds.copyBtnCopyIcon} />
                )}
              </IconWrapper>
            </CopyToClipboard>
          </Tooltip>
        }
        type="text"
        value={value}
      />
    </FormField>
  );
};
